<template>
    <div aria-live="assertive" class="pointer-events-none fixed inset-0 flex items-end px-4 py-6 sm:p-6 z-50">
        <div class="flex w-full flex-col items-center space-y-4 sm:items-end">
            <transition v-for="toast in toasts" enter-active-class="transform ease-out duration-300 transition" enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2" enter-to-class="translate-y-0 opacity-100 sm:translate-x-0" leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
                <div class="pointer-events-auto w-full max-w-sm rounded-lg bg-white ring-1 shadow-lg ring-black/5">
                    <div class="p-4">
                        <div class="flex items-start">
                            <div class="shrink-0">
                                <img v-if="isImageIcon(toast)" class="size-8 rounded-full" :src="toast.variant" alt="" />
                                <component v-else :is="getIcon(toast.variant)" :class="getIconColor(toast.variant)" class="size-6" aria-hidden="true" />
                            </div>
                            <div class="ml-3 w-0 flex-1">
                                <p class="text-sm font-medium text-gray-900" v-if="toast.heading" v-html="toast.heading"></p>
                                <p :class="{'mt-1' : toast.heading}" class="text-sm text-gray-500" v-if="toast.message" v-html="toast.message"></p>

                                <div class="mt-4 flex space-x-3" v-if="toast.buttons.length > 0 && toast.links">
                                    <button v-for="button in toast.buttons" type="button" :class="getButtonColorLinks(button.variant)" class="inline-flex items-center rounded-md px-2.5 py-1.5 text-sm font-semibold shadow-xs" @click="button.callback">{{ button.text }}</button>
                                </div>
                                <div class="mt-3 flex space-x-7" v-else-if="toast.buttons.length > 0">
                                    <button v-for="button in toast.buttons" type="button" :class="getButtonColor(button.variant)" class="rounded-md text-sm font-medium focus:outline-hidden" @click="button.callback">{{ button.text }}</button>
                                </div>

                            </div>
                            <div class="ml-4 flex shrink-0">
                                <button type="button" @click="removeToast(toast.id)" class="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-hidden">
                                    <span class="sr-only">Close</span>
                                    <XMarkIcon class="size-5" aria-hidden="true" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>

<script setup>
import { storeToRefs } from "pinia";
import { useToastStore } from '../stores/toast-store';
import { CheckCircleIcon, XCircleIcon, ExclamationCircleIcon, InformationCircleIcon, QuestionMarkCircleIcon, HandThumbUpIcon } from '@heroicons/vue/24/outline'
import { XMarkIcon } from '@heroicons/vue/20/solid'

const toastStore = useToastStore();
const { toasts } = storeToRefs(toastStore);
const { removeToast } = toastStore;

const isImageIcon = (toast) => {
    if(toast.variant.trim() === "") return false;

    const notImageVariants = ["success", "danger", "info", "warning", "primary", "error", "message", "question"];
    return !notImageVariants.includes(toast.variant) && toast.variant.startsWith("http");
};

const getIconColor = (variant) => {
	switch (variant) {
        case "primary":
            return "text-indigo-400";
		case "success":
            return "text-green-400";
		case "danger":
        case "error":
            return "text-red-400";
		case "info":
            return "text-blue-400";
		case "warning":
            return "text-yellow-400";
		default:
            return "text-gray-600";
	}
};

const getIcon = (variant) => {
    switch (variant) {
        case "primary":
            return HandThumbUpIcon;
        case "success":
            return CheckCircleIcon;
        case "danger":
        case "error":
            return XCircleIcon;
        case "info":
            return InformationCircleIcon;
        case "warning":
            return ExclamationCircleIcon;
        default:
            return QuestionMarkCircleIcon;
    }
};

const getButtonColorLinks = (variant) => {
    switch (variant) {
        case "primary":
            return "bg-indigo-600 text-white hover:bg-indigo-500 focus-visible:outline-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2";
        case "success":
            return "bg-green-600 text-white hover:bg-green-500 focus-visible:outline-green-600 focus-visible:outline-2 focus-visible:outline-offset-2";
        case "danger":
        case "error":
            return "bg-red-600 text-white hover:bg-red-500 focus-visible:outline-red-600 focus-visible:outline-2 focus-visible:outline-offset-2";
        case "info":
            return "bg-blue-600 text-white hover:bg-blue-500 focus-visible:outline-blue-600 focus-visible:outline-2 focus-visible:outline-offset-2";
        case "warning":
            return "bg-yellow-600 text-white hover:bg-yellow-500 focus-visible:orange-yellow-600 focus-visible:outline-2 focus-visible:outline-offset-2";
        default:
            return "bg-white text-gray-900 hover:text-gray-500 ring-1 ring-gray-300 ring-inset hover:bg-gray-50";
    }
};

const getButtonColor = (variant) => {
    switch (variant) {
        case "primary":
            return "bg-white text-indigo-600 hover:text-indigo-500";
        case "success":
            return "bg-white text-green-600 hover:text-green-500";
        case "danger":
            return "bg-white text-red-600 hover:text-red-500";
        case "info":
            return "bg-white text-blue-600 hover:text-blue-500";
        case "warning":
            return "bg-white text-yellow-600 hover:text-yellow-500";
        default:
            return "bg-white text-gray-700 hover:text-gray-500";
    }
};

</script>


<style lang="scss" scoped>

</style>